/**
 * The grid surface.
 *
 * **One scroll container, not two.** Upstream renders `.grid-view__left` and
 * `.grid-view__right` as separate scroll containers and so renders every row
 * twice, with a synchronisation loop keeping the two in step. Here there is one
 * container, one element per row, and the frozen columns are `position: sticky`
 * -- which halves the DOM and deletes the synchronisation entirely.
 *
 * **No virtualizer.** Which rows are on screen and which rows to hold are two
 * answers to the same arithmetic, and the window store already computes both.
 * A second index calculator over the same scroll position is how a grid ends up
 * rendering rows it never asked for. Rows are a fixed height, so there is
 * nothing left for one to measure.
 *
 * **Absolute positioning, never a transform.** The rows sit at
 * `top: index * rowHeight` inside a spacer as tall as the whole table. A
 * `translateY` on the container would be the usual trick and would break every
 * sticky column beneath it, because a transformed ancestor becomes the
 * containing block for its sticky descendants.
 */
import {
  useEffect,
  useRef,
  useState,
  type ClipboardEvent,
  type KeyboardEvent,
  type UIEvent,
} from 'react'
import {
  ArrowLeftToLine,
  ArrowRightToLine,
  ChevronDown,
  Copy,
  EyeClosed,
  EyeOff,
  KeyRound,
  Loader2,
  Maximize2,
  TriangleAlert,
  Plus,
  Snowflake,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useQueryClient } from '@tanstack/react-query'
import { useStore } from 'zustand'

import type { Field, RowData, ViewDecoration } from '@/api/types'
import { qk } from '@/api/query-keys'
import { cn } from '@/shared/lib/utils'
import { fieldKey } from '@/api/types'
import { editorFor } from '../fields/ui/edit'
import { CellDisplay } from '../fields/ui/read'
import type { FieldContext } from '../fields/behavior/behavior'
import { cellValidationError } from '../fields/behavior/validation'
import {
  MAX_BATCH_ROWS,
  createRow,
  createRows,
  deleteRows,
  updateRows,
} from '../rows/rowMutations'
import { commitCellEdit } from '../rows/rowEdit'
import { releasableRows, type HoldReason } from '../rows/rowHold'
import { rowAt } from '../rows/rowWindow'
import {
  rowMembershipSignature,
  type QueryableView,
  type RowSearch,
} from '../rows/rowQuery'
import {
  MIN_COLUMN_WIDTH,
  columnLayout,
  gridColumns,
  type GridFieldOptions,
  type GridColumn,
} from './columns'
import { hiddenFields } from '../views/field-order'
import { useRowDialog } from '../views/use-row-dialog'
import { mergeFieldOptions } from '../table/field-options'
import { MAX_FROZEN_COLUMNS, useGridSettings } from '../table/grid-settings-mutations'
import { useFieldContext } from '../fields/ui/context'
import {
  cellAtIndexes,
  cellIndexes,
  moveSelection,
  rangeContains,
  rectFor,
  singleCell,
  type Cell,
  type Direction,
} from './selection'
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '@/shared/ui/context-menu'
import { CellEditorLayer, draftFor } from './cell-editor'
import { AggregationFooter, FOOTER_HEIGHT } from './aggregation-footer'
import { AGGREGATION_RAW_TYPE, type AggregationTypeName } from '../aggregations'
import { useViewAggregations } from '../aggregations/queries'
import { GroupColumns } from './group-columns'
import { groupSpans, groupsWidth, rowsEndingAGroup } from './grouping'
import { rowColors, type RowColors } from '../decorations/colors'
import { selectColorStyle } from '../fields/select-options'
import {
  cellsIn,
  copyGrid,
  pasteInto,
  pasteRect,
  pasteReach,
  splitPaste,
} from './clipboard'
import { parseTsv, recallClipboard, rememberClipboard, toTsv } from '../rows/rowClipboard'
import { ConfirmDialog } from '@/shared/components/confirm-dialog'
import { canBePrimaryField } from '../fields/config'
import { DuplicateFieldDialog } from '../table/duplicate-field-dialog'
import { RedactionDialog } from '../redaction/redaction-dialog'
import { useTableRedactions } from '../redaction/queries'
import { useMayGovernTable } from '@/features/application/governance'
import { FieldDialog } from '../table/field-dialog'
import { useChangePrimaryField } from '../table/field-mutations'
import { useUpdateFieldOptions } from '../table/field-options-mutations'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu'
import { RowModal } from '../row/row-modal'
import { SelectionOverlay } from './selection-overlay'
import { createSelectionStore } from './selection-store'
import {
  useRegisteredRowWindow,
  useRowQuery,
  useRowWindow,
  useRowWindowStore,
} from './use-grid'

/** A row nothing colours, which is most of them. */
const NO_COLORS: RowColors = { leftBorder: null, background: null }

/** How wide the row-identifier gutter is, in pixels. */
const GUTTER = 64

/**
 * How tall the header is, in pixels.
 *
 * A number as well as a class because it is load-bearing twice over: the header
 * is sticky and covers the top of the rows, so scrolling a row "into view" has
 * to mean below it rather than under it.
 */
const HEADER_HEIGHT = 36

/**
 * The id a cell carries, and the only thing that names it to a screen reader.
 *
 * `aria-activedescendant` on the grid points at one of these. That is the
 * standard pattern for a composite widget with roving focus, and here it is also
 * what lets the cells stay unaware of the selection: exactly two things read the
 * selection -- the overlay that draws it and the container that announces it.
 */
const cellId = (rowId: number, fieldId: number) => `cell-${rowId}-${fieldId}`

const ARROWS: Record<string, Direction> = {
  ArrowUp: 'up',
  ArrowDown: 'down',
  ArrowLeft: 'left',
  ArrowRight: 'right',
}

/** What the leftmost column of each row shows. */
export type RowIdentifier = 'id' | 'count'

export interface GridViewProps {
  view: QueryableView & {
    /**
     * Narrowed back to a number here, because everything this grid can do
     * beyond showing rows -- opening a row, changing a column, registering for
     * row events -- is addressed by id. A publicly shared view has none: its
     * serialiser reports the slug as the id and a placeholder for the table, so
     * it is read by its own surface rather than by this one.
     */
    id: number
    table_id: number
    row_identifier_type?: RowIdentifier
    frozen_column_count?: number
    /** What colours the rows, which arrives with the view as `decorations`. */
    decorations?: readonly ViewDecoration[]
  }
  fields: readonly Field[]
  fieldOptions: Record<string, GridFieldOptions>
  rowHeight: number
  search: RowSearch
  /**
   * The row opened out, which lives in the address.
   *
   * Owned by the caller for the same reason the view is: a row somebody is
   * looking at is somewhere they are, so it belongs in a link they can send and
   * in the browser's own back button.
   */
  openRowId: number | null
  onOpenRow: (rowId: number | null) => void
}

export function GridView({
  view,
  fields,
  fieldOptions,
  rowHeight,
  search,
  openRowId,
  onOpenRow,
}: GridViewProps) {
  const { t } = useTranslation()
  const store = useRowWindowStore()
  useRowQuery(store, view, search)
  const member = useRegisteredRowWindow(store, {
    tableId: view.table_id,
    viewId: view.id,
    fields,
    view,
  })

  const [selection] = useState(createSelectionStore)
  const queryClient = useQueryClient()
  const updateFieldOptions = useUpdateFieldOptions<GridFieldOptions>(view.id)
  const gridSettings = useGridSettings(view.table_id, view.id)
  const changePrimary = useChangePrimaryField(view.table_id)
  // The divider being dragged, if one is. Held here rather than in the header
  // so the width follows the pointer without a request per pixel: the columns
  // are drawn from it, and the one request goes when the button comes up.
  const [resizing, setResizing] = useState<Resize | null>(null)
  // The field whose settings are open, or where a new one is being made. Not in
  // the address: it is a change somebody is making rather than somewhere they
  // are, and a link to a half-written formula is a link to nothing.
  const [editingField, setEditingField] = useState<Field | null>(null)
  // Where a field being made should land, when it is being made from a column's
  // own menu rather than from the end of the header. `null` while nothing is
  // being made; a position of `null` means "at the end".
  const [making, setMaking] = useState<{ beside: number | null } | null>(null)
  // The field being copied, and the one being offered the primary role. Both
  // are questions rather than places, so neither is in the address.
  const [duplicating, setDuplicating] = useState<Field | null>(null)
  const [promoting, setPromoting] = useState<Field | null>(null)
  const [redacting, setRedacting] = useState<Field | null>(null)

  // Which columns are withheld from *this* reader. Their cells arrive as the
  // literal string `[REDACTED]`, so a date or a number reader handed one would
  // draw a column that looks broken rather than one that looks withheld.
  const redacted = new Set(useTableRedactions(view.table_id).data ?? [])
  // Whether this reader may set that, which is a different question: the
  // columns withheld from them are theirs to know, and who a column is withheld
  // from is the owning database's to say.
  const mayGovern = useMayGovernTable(fields[0]?.workspace_id, view.table_id)
  const scroller = useRef<HTMLDivElement>(null)

  // Read off a field rather than passed in: every field carries the workspace
  // it belongs to, and a table always has at least its primary one. The
  // alternative is a prop threaded from the page for a fact the data already
  // holds.
  const context = useFieldContext(fields[0]?.workspace_id)
  const count = useRowWindow(store, (state) => state.window.count)
  const groupByMetadata = useRowWindow(store, (state) => state.groupByMetadata)
  const visible = useRowWindow(store, (state) => state.visible)
  const window = useRowWindow(store, (state) => state.window)
  // Subscribed separately from the rows, so a badge appearing re-renders the
  // grid without the window having changed -- and so the common case, where
  // nothing is held, costs one map lookup per row and no work at all.
  const heldRows = useRowWindow(store, (state) => state.held)
  const savingRows = useRowWindow(store, (state) => state.saving)
  const holdOn = (rowId: number | undefined) =>
    rowId === undefined ? undefined : heldRows.get(rowId)
  // The one other subscriber to the selection, and it reads a string rather than
  // the range: the grid re-renders when the *active cell* changes and not when
  // a range grows around it.
  const active = useStore(selection, (state) =>
    state.range === null
      ? undefined
      : cellId(state.range.head.rowId, state.range.head.fieldId),
  )

  const frozenCount = view.frozen_column_count ?? 1
  const columns = gridColumns(
    fields,
    // The drag is drawn by pretending the option it is going to write has
    // already been written, so a width being dragged and a width that was
    // stored reach the column through one path rather than two.
    resizing === null
      ? fieldOptions
      : mergeFieldOptions<GridFieldOptions>(fieldOptions, {
          [resizing.fieldId]: { width: resizing.width },
        }),
    { frozenCount },
  )
  // The group columns sit to the left of everything, so they move the origin
  // every other measurement is taken from. Zero when the view is not grouped,
  // which is what keeps an ungrouped grid laid out exactly as it was.
  const groupBys = view.group_bys ?? []
  const gutterLeft = groupsWidth(groupBys)

  // The narrower half of the row query's signature: which rows, in any order.
  // Sorting or grouping the view leaves every number under it where it was.
  const membership = rowMembershipSignature(view, search)
  // Asked for only when a column is showing one. A view with none answers with
  // the row count and nothing else, which is a request per table nobody reads.
  const showsAggregations = Object.values(fieldOptions).some(
    (option) => (option.aggregation_raw_type ?? '') !== '',
  )
  const aggregations = useViewAggregations(view.id, membership, search, {
    // And not while a field-options write is in flight. The endpoint is not
    // told what to compute -- it reads the `aggregation_raw_type` it has
    // stored -- so a request that overtakes the write asking for a new one is
    // answered about the old one, and that answer is what gets cached.
    enabled: showsAggregations && !updateFieldOptions.isPending,
  })
  const { offsets, width } = columnLayout(columns, gutterLeft + GUTTER)

  // Measured rather than assumed: the window's padding is half a viewport, so a
  // container whose height is wrong holds the wrong number of rows. Reported on
  // every resize because the sidebar can be collapsed and the browser resized.
  useEffect(() => {
    const element = scroller.current
    if (element === null) return

    const report = () => {
      store.getState().setViewport({ height: element.clientHeight, rowHeight })
    }
    report()

    const observer = new ResizeObserver(report)
    observer.observe(element)
    return () => {
      observer.disconnect()
    }
  }, [store, rowHeight])

  /**
   * Follows the pointer while a column divider is being dragged.
   *
   * On the window rather than on the divider, because a drag that leaves the
   * element -- which every drag does, immediately -- would otherwise stop being
   * reported. Mouse events rather than pointer events, because a pointer
   * capture is the other way to say this and jsdom implements none of it, so
   * the specs could not press the divider at all.
   *
   * The listeners are registered on the first movement and removed when the
   * button comes up; the drag itself is read through a ref, so the handlers do
   * not have to be replaced on every pixel.
   */
  const dragging = resizing !== null
  const heldResize = useRef<Resize | null>(null)
  heldResize.current = resizing
  const finishResize = useRef<(at: Resize) => void>(undefined)
  finishResize.current = (at) => {
    updateFieldOptions.mutate({ [at.fieldId]: { width: at.width } })
  }

  useEffect(() => {
    if (!dragging) return

    const move = (event: MouseEvent) => {
      const at = heldResize.current
      if (at === null) return
      // Clamped here rather than only where the column is drawn. Drawing it
      // wide enough while sending what the pointer literally said would send a
      // negative width to a field the backend declares as a positive integer.
      setResizing({
        ...at,
        width: Math.max(MIN_COLUMN_WIDTH, at.startWidth + event.clientX - at.from),
      })
    }
    const up = () => {
      const at = heldResize.current
      setResizing(null)
      // A press with no movement is not a resize. Sending one would be a
      // request, an undo entry and a broadcast for a click on a divider.
      if (at !== null && at.width !== at.startWidth) finishResize.current?.(at)
    }

    // `globalThis` rather than `window`, which in this component is the row
    // window -- the buffer, not the browser.
    //
    // Both listeners are hung off one signal so that ending the drag is a
    // single statement. Nothing a test can watch distinguishes a leaked
    // listener from a removed one -- the handlers read the drag through a ref,
    // and there is no drag -- so the way to be sure of the cleanup is for there
    // to be only one thing to get right.
    const stop = new AbortController()
    globalThis.addEventListener('mousemove', move, { signal: stop.signal })
    globalThis.addEventListener('mouseup', up, { signal: stop.signal })
    return () => {
      stop.abort()
    }
  }, [dragging])

  /**
   * Puts a newly made field where it was asked for.
   *
   * A field is created at the end of the table -- the endpoint takes no
   * position -- so landing it beside another one is a second write, to the
   * *view's* field options. That is the only place a column's place is stored:
   * the field's own `order` belongs to the table, and two views of one table
   * show their columns in different orders on purpose.
   *
   * Every visible column is renumbered rather than only the new one, because
   * the orders in between are whatever they happen to be and inserting into
   * them needs numbers that do not exist.
   */
  function placeField(made: Field, beside: number | null): void {
    const order = columns.map((column) => column.field.id)
    // One check, not two. "Nothing to place it against" and "the column it was
    // to go beside is not here any more" are the same answer -- do nothing --
    // and writing them separately is a branch no test can tell from its
    // absence, because the second catches everything the first would.
    const at = beside === null ? -1 : order.indexOf(beside)
    if (at === -1) return
    order.splice(at, 0, made.id)

    updateFieldOptions.mutate(
      Object.fromEntries(
        order
          // The primary field's place is a rule rather than a stored number --
          // `orderFields` pins it to the front -- so writing one for it would
          // be a second answer to a question that already has one.
          .filter((id) => fields.find((one) => one.id === id)?.primary !== true)
          .map((id, index) => [id, { order: index + 1 }]),
      ),
    )
  }

  function hideField(field: Field): void {
    updateFieldOptions.mutate({ [field.id]: { hidden: true } })
  }

  /**
   * Freezes the columns up to and including one, or unfreezes back past it.
   *
   * A count of *visible* columns from the left rather than a set of field ids,
   * which is the backend's model and the reason hiding a frozen column
   * promotes the next one instead of narrowing the frozen section. So the
   * column at position `index` is the boundary when the count is `index + 1`,
   * and unfreezing it means freezing everything before it.
   */
  function freezeUpTo(index: number, frozen: boolean): void {
    gridSettings.setFrozenColumns(frozen ? index : index + 1)
  }

  /**
   * Changes what a column works out, and asks again only if it has to.
   *
   * Eight of the nineteen aggregations are `empty_count` read a different way,
   * so switching between "filled" and "percent filled" changes the arithmetic
   * and not the question: the number is already in hand and the footer redraws
   * from it. Only a change of *raw* type is a change of what the server will
   * compute, and only that costs a request.
   */
  function chooseAggregation(field: Field, type: AggregationTypeName | null): void {
    const before = fieldOptions[String(field.id)]?.aggregation_raw_type ?? ''
    const raw = type === null ? '' : AGGREGATION_RAW_TYPE[type]

    updateFieldOptions.mutate(
      { [field.id]: { aggregation_type: type ?? '', aggregation_raw_type: raw } },
      {
        // After the write has landed, not alongside it: the server answers
        // from the options it has stored, so asking any earlier asks the
        // question this change is replacing.
        onSuccess: () => {
          if (raw === before) return
          // Every entry for this view, not only the one on screen: the answer
          // held for the unfiltered view is about to be as wrong as this one.
          void queryClient.invalidateQueries({ queryKey: qk.aggregationsOf(view.id) })
        },
      },
    )
  }

  /**
   * Gives the grid its focus back when a row stops being open.
   *
   * In the cleanup rather than in the dialog's own close handler, because that
   * handler runs while the dialog is still mounted and still trapping the
   * focus -- so the grid takes it and the dialog pulls it straight back, and
   * then unmounts and leaves it nowhere. A cleanup runs after the dialog has
   * gone. The button that opened the row is out of the tab order, so there is
   * nowhere else for the focus to sensibly land.
   */
  useEffect(() => {
    if (openRowId === null) return
    return () => scroller.current?.focus()
  }, [openRowId])

  /**
   * Clears the selection when a click lands outside the grid.
   *
   * A rectangle drawn while the focus is somewhere else claims something is
   * active that is not, and nothing outside this grid acts on a cell selection
   * -- the row actions are in its own context menu. Menus and dialogs are
   * excluded because both are portaled out of the grid: the context menu is
   * *about* the selection, and the row dialog is opened from it and hands the
   * focus back when it closes.
   *
   * On mousedown rather than on blur, which is the same distinction the editor
   * makes: blur fires when the row dialog takes the focus, and losing your
   * place because you opened a row is not what clicking away means.
   */
  useEffect(() => {
    const clearOnOutsideClick = (event: MouseEvent): void => {
      const target = event.target
      if (!(target instanceof Node)) return
      if (scroller.current?.contains(target) === true) return
      if (
        target instanceof Element &&
        target.closest('[role="dialog"], [role="menu"]') !== null
      ) {
        return
      }
      // Not while something is being edited. Losing focus is what commits an
      // edit, and clearing here would run first and throw away what was typed
      // -- so clicking away from a half-typed cell keeps the place it was in,
      // and clicking away from a selection clears it.
      if (selection.getState().editing !== null) return

      selection.getState().clear()
    }

    document.addEventListener('mousedown', clearOnOutsideClick)
    return () => {
      document.removeEventListener('mousedown', clearOnOutsideClick)
    }
  }, [selection])

  /**
   * Lets go of held rows the selection has left.
   *
   * A row is held because somebody is on it, so the rule for letting go is
   * exactly "they are not any more". Subscribed to the selection store rather
   * than driven from the places that move the selection: there are five of
   * those -- clicking, arrowing, tabbing, escaping, the row modal closing --
   * and this way none of them has to remember.
   */
  useEffect(() => {
    const releaseLeftRows = (): void => {
      const { held } = store.getState()
      if (held.size === 0) return

      const staying = new Set(selectedRowIds())
      const releasing = releasableRows(held, staying)
      if (releasing.length > 0) store.getState().releaseRows(releasing)
    }

    return selection.subscribe(releaseLeftRows)
    // `selectedRowIds` reads both stores at call time rather than closing over
    // their contents, so this subscribes once and stays correct.
  }, [selection, store])

  const onScroll = (event: UIEvent<HTMLDivElement>) => {
    store.getState().scrolled(event.currentTarget.scrollTop)
  }

  /**
   * Keeps the selected cell on screen, which is also what keeps the buffer fed.
   *
   * Scrolling fires the handler above, which plans the window and asks for
   * whatever the move has brought into range -- so arrowing down a table pulls
   * the next block in without anything here knowing that is what it is doing.
   */
  function revealRow(rowIndex: number): void {
    const element = scroller.current
    if (element === null) return

    const top = rowIndex * rowHeight
    // The header sits over the top of the rows and the footer over the bottom,
    // so a row scrolled flush to either edge of the container is a row
    // underneath one of them.
    const above = element.scrollTop + HEADER_HEIGHT
    const below = element.scrollTop + element.clientHeight - FOOTER_HEIGHT

    if (top < above) element.scrollTop = top - HEADER_HEIGHT
    else if (top + rowHeight > below) {
      element.scrollTop = top + rowHeight + FOOTER_HEIGHT - element.clientHeight
    }
  }

  function moveBy(direction: Direction, extend: boolean): void {
    const range = selection.getState().range
    if (range === null) return

    const held = store.getState().window
    const moved = moveSelection(range, direction, held, columns, { extend })
    selection.getState().replace(moved)

    const indexes = cellIndexes(moved.head, held, columns)
    if (indexes !== null) revealRow(indexes.row)
  }

  /**
   * Opens the editor on the selected cell.
   *
   * `initial` is what to start from: a character somebody typed straight into
   * the grid replaces the value, the way it does in every spreadsheet, while
   * Enter and a double-click open the value that is there.
   */
  function openEditor(initial?: string): void {
    const range = selection.getState().range
    if (range === null) return

    const column = columns.find((c) => c.field.id === range.head.fieldId)
    if (column === undefined) return

    const editor = editorFor(column.field)
    if (editor === null) return

    // An editor that cannot fit in a cell is clipped by the container the grid
    // draws it in, so the row is opened instead, where there is room for it. A
    // character typed at one of these does nothing: the row is opened by Enter
    // and by a double-click, and opening it on every keystroke would be a
    // dialog somebody could not type past.
    if (editor.needsRoom === true) {
      if (initial === undefined) onOpenRow(range.head.rowId)
      return
    }

    const held = store.getState().window
    const row = held.rows.find((candidate) => candidate.id === range.head.rowId)
    if (row === undefined) return

    selection.getState().edit(range.head, initial ?? draftFor(column.field, row, held))
  }

  /**
   * Sends what was typed, if it is different from what was there.
   *
   * The comparison matters more than it looks, and it lives in
   * `commitCellEdit` -- shared with the row modal, which opens the same
   * editors and would otherwise have to answer the same question a second
   * time. Opening a cell and closing it again is something people do
   * constantly, by accident and on purpose, and every one of those would
   * otherwise be a write, a row history entry and a broadcast to everybody
   * else looking at the table.
   */
  function commit(): boolean {
    const editing = selection.getState().editing
    if (editing === null) return true

    const column = columns.find((c) => c.field.id === editing.cell.fieldId)
    if (column === undefined) {
      selection.getState().stopEditing()
      return true
    }

    // Refused before anything is sent, and the editor stays open on what was
    // typed. The alternative is a round trip that ends in a toast and a value
    // reverting -- so the correction has to be typed again from memory, and the
    // cell that would not take it is no longer the one being looked at.
    if (cellValidationError(column.field, editing.draft) !== null) return false

    selection.getState().stopEditing()

    const held = store.getState().window
    const row = held.rows.find((candidate) => candidate.id === editing.cell.rowId)
    if (row === undefined) return true

    // Held while the selection is still on it. The commit runs before the
    // selection moves -- `focus()` dispatches blur synchronously -- so this row
    // is the one about to be left, and holding it is what stops it vanishing
    // between the keystroke and the arrow key. Asked again when the server
    // answers, by which time the selection has usually gone somewhere else.
    void commitCellEdit(member, row, column.field, editing.draft, context, {
      isHeld: isSelectedRow,
    })?.catch(() => {
      // The row has already been put back by the time this runs; all that is
      // left is to say so, because a value that reverts with no explanation
      // reads as the application losing what was typed.
      toast.error(t('grid.editFailed'))
    })
    return true
  }

  function focusGrid(): void {
    scroller.current?.focus()
  }

  /**
   * The rows the selection spans, which is what a row action acts on.
   *
   * A selection of one cell is one row, and a rectangle dragged down five is
   * five -- so "delete row" and "delete five rows" are the same command with a
   * different count, rather than two.
   */
  /**
   * Whether the selection is on a row *now*, which is what a hold turns on.
   *
   * Passed as a function rather than the answer, so an edit whose request is
   * still out asks again when it lands rather than acting on where the
   * selection was when somebody pressed the key.
   */
  function isSelectedRow(rowId: number): boolean {
    return selectedRowIds().includes(rowId)
  }

  function selectedRowIds(): number[] {
    const range = selection.getState().range
    if (range === null) return []

    const held = store.getState().window
    const rect = rectFor(range, held, columns)
    if (rect === null) return []

    return cellsIn(rect, held, columns).rows.map((row) => row.id)
  }

  /**
   * Adds a row below the one given, or at the end of the view when given none.
   *
   * The index is where it goes in the window; the server is told which row it
   * goes *above*, because that is the only thing that means anything to a view
   * the client holds a hundred rows of.
   */
  function onCreate(afterIndex?: number): void {
    const held = store.getState().window
    const index = afterIndex === undefined ? held.count : afterIndex + 1
    const before = rowAt(held, index)

    void createRow(member, {
      index,
      ...(before === undefined ? {} : { before: before.id }),
      onIdResolved: (temporaryId, realId) => {
        selection.getState().renameRow(temporaryId, realId)
      },
      isHeld: (rowId) => selectedRowIds().includes(rowId),
    }).catch(() => {
      toast.error(t('grid.createFailed'))
    })

    // Select the new row's first cell, so it can be typed into straight away.
    // Read back from the window rather than from the promise: the row is put
    // there with a temporary id before the request is sent, which is the whole
    // point of it appearing instantly, and waiting for the answer would leave
    // the row on screen for a moment with nothing selected.
    const created = rowAt(store.getState().window, index)
    const first = columns[0]
    if (created !== undefined && first !== undefined) {
      selection
        .getState()
        .replace(singleCell({ rowId: created.id, fieldId: first.field.id }))
      focusGrid()
    }
  }

  function onDelete(rowIds: readonly number[]): void {
    if (rowIds.length === 0) return
    // Nothing is selected afterwards, because what was selected is gone. A
    // selection pointing at deleted rows draws nothing and moves nowhere.
    selection.getState().clear()

    void deleteRows(member, rowIds).catch(() => {
      toast.error(t('grid.deleteFailed', { count: rowIds.length }))
    })
  }

  /**
   * Writes the selection to the clipboard.
   *
   * Two things go: the rendered text, which is what every other application
   * receives, and the raw values, which are what a paste back into this one
   * restores. The clipboard itself only carries the text -- the values are kept
   * beside it and matched against the text on the way back.
   */
  function onCopy(event: ClipboardEvent): void {
    const range = selection.getState().range
    if (range === null) return

    const held = store.getState().window
    const rect = rectFor(range, held, columns)
    if (rect === null) return

    const { rows: selectedRows, columns: selectedColumns } = cellsIn(rect, held, columns)
    const { text, values } = copyGrid(selectedRows, selectedColumns, context)
    const tsv = toTsv(text)

    event.preventDefault()
    event.clipboardData?.setData('text/plain', tsv)
    rememberClipboard({ text: tsv, values })
  }

  /**
   * Writes a rectangle from the clipboard into the grid.
   *
   * The pasted block decides the size, clipped to the rows and columns below
   * and to the right of where the selection starts. Anything past those is
   * dropped rather than written somewhere else -- growing the table needs row
   * creation, which is its own mutation.
   */
  function onPaste(event: ClipboardEvent): void {
    const range = selection.getState().range
    if (range === null) return

    const text = event.clipboardData?.getData('text/plain') ?? ''
    const values = recallClipboard(text)
    // Empty text with nothing remembered for it is an empty clipboard. Empty
    // text *with* something remembered is a cell somebody copied that had
    // nothing in it, and pasting that should empty what it lands on.
    if (text === '' && values === null) return

    event.preventDefault()

    const held = store.getState().window
    const start = rectFor(range, held, columns)
    if (start === null) return

    const pastedText = parseTsv(text)
    const rect = pasteRect(
      start,
      values ?? pastedText,
      columns,
      pasteReach(start, held, MAX_BATCH_ROWS),
    )
    const { existing, created } = splitPaste(rect, held)

    // A paste longer than the table grows it. It is cut short for either of two
    // reasons: more rows than one request may carry -- several requests would
    // be several undo entries and several broadcasts for one action -- or more
    // rows than the client is holding ids for.
    if ((values ?? pastedText).length > rect.rows) {
      toast.warning(t('grid.pasteClipped', { count: rect.rows }))
    }

    const { rows: targetRows, columns: targetColumns } = cellsIn(
      { ...rect, rows: existing },
      held,
      columns,
    )
    const updates = pasteInto(
      { text: pastedText, values },
      targetRows,
      targetColumns,
      context,
    )

    // The rows past the end of the view are made with their values rather than
    // made and then written to: one request per half rather than one per row.
    const overflow = pasteInto(
      { text: pastedText.slice(existing), values: values?.slice(existing) ?? null },
      Array.from({ length: created }, (_, i) => ({ id: -1 - i }) as never),
      targetColumns,
      context,
    ).map(({ values: made }) => made)

    if (updates.length === 0 && overflow.length === 0) return

    // Selecting what was written, which is how somebody sees how far a paste
    // reached -- and how they see that it was cut short, when it was.
    const anchor = cellAtIndexes(held, columns, { row: rect.top, column: rect.left })
    const head = cellAtIndexes(held, columns, {
      row: rect.top + existing - 1,
      column: rect.left + rect.columns - 1,
    })
    if (anchor !== null && head !== null) selection.getState().replace({ anchor, head })

    void Promise.all([
      updates.length > 0
        ? updateRows(member, updates, {
            // The pasted rows are the selected ones, so any of them that stops
            // matching is held rather than pulled out from under the paste.
            isHeld: isSelectedRow,
          })
        : undefined,
      overflow.length > 0
        ? createRows(member, { values: overflow, index: held.count })
        : undefined,
    ]).catch(() => {
      toast.error(t('grid.editFailed'))
    })
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    // While the editor is open every key belongs to it, and the handler it is
    // given stops the ones it uses from arriving here at all.
    if (selection.getState().editing !== null) return

    const direction = ARROWS[event.key]
    if (direction !== undefined) {
      // Before the move rather than after it: the browser scrolls a focused
      // container on an arrow key, and the grid decides where it goes.
      event.preventDefault()
      moveBy(direction, event.shiftKey)
      return
    }

    if (event.key === 'Escape') {
      selection.getState().clear()
      return
    }

    if (event.key === 'Delete' || event.key === 'Backspace') {
      // Empties every cell in the range, which is what Delete means in a grid.
      // Written through the same batch as a paste: one request, one undo entry
      // and one broadcast for something the person did once.
      event.preventDefault()
      const range = selection.getState().range
      if (range === null) return

      const held = store.getState().window
      const rect = rectFor(range, held, columns)
      if (rect === null) return

      const { rows: clearedRows, columns: clearedColumns } = cellsIn(rect, held, columns)
      const updates = clearedRows.map((row) => ({
        rowId: row.id,
        values: Object.fromEntries(
          clearedColumns
            // A column the server owns has no value to clear, and sending one
            // would be refused.
            .filter((column) => column.field.read_only !== true)
            .map((column) => [
              fieldKey(column.field.id),
              editorFor(column.field)?.fromDraft(column.field, '', context) ?? null,
            ]),
        ),
      }))
      if (updates.length === 0) return

      void updateRows(member, updates, { isHeld: isSelectedRow }).catch(() => {
        toast.error(t('grid.editFailed'))
      })
      return
    }

    if (event.key === 'Tab') {
      // Along the row, the way it already does from inside the editor. Without
      // this the browser takes it, and since the grid is deliberately one tab
      // stop, Tab on a selected cell left the grid entirely -- which is not
      // what Tab means anywhere a spreadsheet is being read.
      event.preventDefault()
      moveBy(event.shiftKey ? 'left' : 'right', false)
      return
    }

    if (event.key === 'Enter') {
      event.preventDefault()
      // Enter opens the cell and shift-Enter opens the row, which is the only
      // way to reach the dialog from the keyboard: the buttons that open it
      // are one per row, and a grid with a tab stop in every row is a grid
      // nobody can tab past.
      if (event.shiftKey) {
        const range = selection.getState().range
        if (range !== null) onOpenRow(range.head.rowId)
        return
      }
      openEditor()
      return
    }

    // A printable character opens the editor and replaces the value with it.
    // The modifier check is what keeps a copy or a paste from being read as
    // somebody typing a "c".
    if (event.key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey) {
      event.preventDefault()
      openEditor(event.key)
    }
  }

  function onEditorKeyDown(event: KeyboardEvent<HTMLElement>): void {
    if (event.key === 'Escape') {
      // Stopped here rather than allowed to bubble: the grid reads Escape as
      // "deselect", and cancelling an edit should leave the cell selected.
      event.preventDefault()
      event.stopPropagation()
      selection.getState().stopEditing()
      focusGrid()
      return
    }

    if (event.key === 'Enter' || event.key === 'Tab') {
      event.preventDefault()
      event.stopPropagation()
      // Asked here rather than left to the blur, because the answer decides
      // whether the selection may move at all: an editor holding a value the
      // cell will not take stays open on it, and moving on would leave the
      // message pointing at a cell nobody is in.
      if (!commit()) return
      // Focusing the grid blurs the editor, and blurring the editor is what
      // commits it -- synchronously, so the new value is in the window before
      // the selection moves onto the next cell. It has already been committed
      // above by then, and committing again finds nothing to do.
      focusGrid()
      // Down for Enter, along for Tab: the two ways a spreadsheet lets somebody
      // fill in a column and fill in a row without reaching for the mouse.
      moveBy(event.key === 'Enter' ? 'down' : 'right', false)
    }
  }

  /**
   * Moves the selection onto a cell, and says whether it was already there.
   *
   * The answer is what decides whether a click on a link inside the cell
   * follows it. A url column whose first click both selected the cell and left
   * the page could not be read at all -- so the first click selects and the
   * next one follows, which is what a spreadsheet does. Answered from here
   * rather than read in the row, because a row that subscribed to the selection
   * would re-render every time it moved, which is the whole thing the overlay
   * exists to avoid.
   */
  /**
   * Extends the selection to a cell being dragged over.
   *
   * Its own path rather than `onSelect` with `extend: true`, because the answer
   * that one returns -- whether the cell was already selected -- decides
   * whether a click follows a link, and a drag is never that.
   */
  function onExtend(cell: Cell): void {
    selection.getState().extendTo(cell)
  }

  function onSelect(
    cell: Cell,
    { extend, secondary }: { extend: boolean; secondary: boolean },
  ): boolean {
    // No commit here. Pressing the mouse down on another cell takes focus off
    // the editor, and losing focus is what commits it -- so clicking away from
    // a half-typed cell is already the same path as clicking outside the grid
    // altogether. Committing here as well would be a second route to the same
    // place, indistinguishable from the first.
    const range = selection.getState().range
    const wasHere =
      range !== null &&
      range.head.rowId === cell.rowId &&
      range.head.fieldId === cell.fieldId

    // A right-click inside the selection leaves it alone, so that the menu it
    // opens can act on the whole of it. Outside, it moves the selection first,
    // which is what makes the menu about the row it landed on.
    if (
      secondary &&
      range !== null &&
      rangeContains(range, cell, store.getState().window, columns)
    ) {
      return wasHere
    }

    if (extend) selection.getState().extendTo(cell)
    else selection.getState().replace(singleCell(cell))
    return wasHere
  }

  const indexes: number[] = []
  for (let index = visible.start; index < visible.end; index += 1) indexes.push(index)

  // Over the rows on screen, because that is what there is: a run is as long as
  // the part of the group the buffer is holding, and the count beside it is how
  // long the whole group is. Both are recomputed on every render, which is
  // cheap -- it is a walk down at most a hundred rows -- and is what makes them
  // follow an edit that moves a row from one group to another.
  const groupLevels = groupSpans(groupBys, fields, groupByMetadata, visible, (index) =>
    rowAt(window, index),
  )
  const endsGroup = rowsEndingAGroup(groupBys, fields, visible, (index) =>
    rowAt(window, index),
  )

  // What colour each row is drawn in, if the view says any. Worked out per row
  // rather than cached, for the reason the group runs are: it is a walk over at
  // most a hundred rows, and it has to follow an edit that changes whether a
  // row still matches a colour's conditions.
  const decorations = view.decorations ?? []

  /**
   * The colours one row is drawn in.
   *
   * Worked out here rather than in the row, because it needs the table's
   * *fields* -- a colour's conditions may name a column this view does not show
   * -- and the row only ever knows about its own columns.
   */
  const colorsAt = (index: number): RowColors => {
    const row = rowAt(window, index)
    // A row that has not arrived is drawn plain. There is nothing to match
    // against, and guessing would change the colour under the reader the
    // moment it landed.
    if (row === undefined) return NO_COLORS
    return rowColors(decorations, row, fields, context)
  }

  // Which row the dialog is showing, where it came from when the window is not
  // holding it, and whether there is one either side. Shared with every other
  // view type that can open a row -- see `useRowDialog`.
  const openRow = useRowDialog({
    window,
    count,
    member,
    view,
    fields,
    openRowId,
    onOpenRow,
    search,
  })

  const surface = (
    <div
      ref={scroller}
      onScroll={onScroll}
      role="grid"
      aria-label={t('grid.label')}
      // Focusable as one widget rather than one stop per cell: a grid whose
      // every cell is in the tab order is a grid nobody can tab past.
      tabIndex={0}
      aria-activedescendant={active}
      onKeyDown={onKeyDown}
      onCopy={onCopy}
      onPaste={onPaste}
      // One past the rows, because the header is a row too. Assistive
      // technology reads these to say "row 812 of 40,000" for a row that is the
      // only one of the forty thousand in the document.
      aria-rowcount={count + 1}
      aria-colcount={columns.length + 1}
      className="relative h-full overflow-auto"
    >
      {/* A column at least as tall as what can be seen, so that the footer's
          `mt-auto` has free space to take and lands at the bottom of the
          window. Without it the table is as tall as its rows, and a table of
          five rows leaves its aggregations stranded under the fifth with the
          rest of the window blank beneath them. */}
      <div className="flex min-h-full flex-col" style={{ width }}>
        <GridHeader
          columns={columns}
          offsets={offsets}
          gutterLeft={gutterLeft}
          onEditField={setEditingField}
          onHideField={hideField}
          onFreezeTo={freezeUpTo}
          onInsertField={(beside) => {
            setMaking({ beside })
          }}
          onDuplicateField={setDuplicating}
          onMakePrimary={setPromoting}
          onRedactField={setRedacting}
          mayGovern={mayGovern}
          redacted={redacted}
          onStartResize={setResizing}
        />

        {/* One row taller than the table, for the button that adds one. */}
        <div className="relative shrink-0" style={{ height: (count + 1) * rowHeight }}>
          {indexes.map((index) => (
            <GridRow
              key={index}
              index={index}
              row={rowAt(window, index)}
              colors={colorsAt(index)}
              columns={columns}
              offsets={offsets}
              gutterLeft={gutterLeft}
              endsGroup={endsGroup}
              rowHeight={rowHeight}
              identifier={view.row_identifier_type ?? 'id'}
              context={context}
              onSelect={onSelect}
              onExtend={onExtend}
              onOpen={openEditor}
              onExpand={onOpenRow}
              onDelete={onDelete}
              onCreate={onCreate}
              selectedRowIds={selectedRowIds}
              heldReason={holdOn(rowAt(window, index)?.id)?.reason}
              saving={savingRows.has(rowAt(window, index)?.id ?? -1)}
            />
          ))}
          {/* After the rows in the document, so that the cells scrolling past
              underneath it are painted under it rather than over it. */}
          <GroupColumns
            groupBys={groupBys}
            fields={fields}
            levels={groupLevels}
            rowHeight={rowHeight}
            context={context}
          />

          <button
            type="button"
            onClick={() => {
              onCreate()
            }}
            className="absolute flex items-center gap-2 border-b px-2 text-sm text-muted-foreground hover:bg-muted/40"
            style={{
              top: count * rowHeight,
              height: rowHeight,
              left: gutterLeft,
              width: width - gutterLeft,
            }}
          >
            <Plus className="size-4" />
            {t('grid.newRow')}
          </button>

          <SelectionOverlay
            store={selection}
            window={window}
            columns={columns}
            rowHeight={rowHeight}
            gutter={gutterLeft + GUTTER}
          />
          <CellEditorLayer
            store={selection}
            window={window}
            columns={columns}
            rowHeight={rowHeight}
            gutter={gutterLeft + GUTTER}
            context={context}
            onKeyDown={onEditorKeyDown}
            onCommit={commit}
          />
        </div>

        {/* After the rows, and outside the spacer they are positioned in: it
            sits at the bottom of the scroll container rather than at the bottom
            of a table that is forty thousand rows tall. */}
        <AggregationFooter
          columns={columns}
          offsets={offsets}
          gutterLeft={gutterLeft}
          gutter={GUTTER}
          options={fieldOptions}
          aggregations={aggregations.data}
          context={context}
          onChoose={chooseAggregation}
        />
      </div>
    </div>
  )

  // Outside the grid element rather than inside it, and that is not
  // cosmetic. A React portal delivers its events through the *React* tree, not
  // the DOM one -- so a dialog written inside the grid is still inside it as
  // far as every handler is concerned, and every character typed into a field
  // would reach the grid's own key handler, be read as somebody typing into a
  // cell, and be swallowed before the box ever saw it.
  return (
    <>
      {surface}
      {editingField !== null && (
        <FieldDialog
          tableId={view.table_id}
          viewId={view.id}
          // Keyed on the field, so the dialog is rebuilt when it is opened on
          // a different column rather than keeping the last one's boxes.
          key={editingField.id}
          field={
            // Read out of the current list rather than held: a field renamed
            // by somebody else while the dialog is open is still the field
            // this dialog is about.
            fields.find((candidate) => candidate.id === editingField.id) ?? editingField
          }
          onClose={() => {
            setEditingField(null)
          }}
        />
      )}

      {duplicating !== null && (
        <DuplicateFieldDialog
          field={duplicating}
          onClose={() => {
            setDuplicating(null)
          }}
        />
      )}

      {redacting !== null && (
        <RedactionDialog
          tableId={view.table_id}
          field={redacting}
          onClose={() => {
            setRedacting(null)
          }}
        />
      )}

      {promoting !== null && (
        <ConfirmDialog
          open
          onOpenChange={() => {
            setPromoting(null)
          }}
          title={t('field.makePrimaryTitle', { field: promoting.name })}
          description={t('field.makePrimaryDescription')}
          confirmLabel={t('field.makePrimary')}
          onConfirm={() => {
            changePrimary.mutate(promoting.id, {
              onError: () => {
                toast.error(t('field.makePrimaryFailed'))
              },
            })
            setPromoting(null)
          }}
        />
      )}

      {making !== null && (
        <FieldDialog
          tableId={view.table_id}
          viewId={view.id}
          // Keyed on where it is going, so asking for a column to the left of
          // one and then to the left of another starts from a blank both times.
          key={`new-${String(making.beside)}`}
          field={null}
          onCreated={(made) => {
            placeField(made, making.beside)
          }}
          onClose={() => {
            setMaking(null)
          }}
        />
      )}

      {openRowId !== null && (
        <RowModal
          member={openRow.member}
          rowId={openRowId}
          row={openRow.row}
          missing={openRow.missing}
          fields={columns.map((column) => column.field)}
          hidden={hiddenFields(fields, fieldOptions)}
          context={context}
          steps={openRow.steps}
          onStep={openRow.onStep}
          onClose={() => {
            onOpenRow(null)
          }}
        />
      )}
    </>
  )
}

/** A column divider being dragged: which one, from where, and to what width. */
interface Resize {
  fieldId: number
  /** Where the pointer was when the drag started. */
  from: number
  startWidth: number
  width: number
}

function GridHeader({
  columns,
  offsets,
  gutterLeft,
  onEditField,
  onHideField,
  onFreezeTo,
  onInsertField,
  onDuplicateField,
  onMakePrimary,
  onRedactField,
  mayGovern,
  redacted,
  onStartResize,
}: {
  columns: readonly GridColumn[]
  offsets: readonly number[]
  /** How far in the gutter starts, which is the width of the group columns. */
  gutterLeft: number
  onEditField: (field: Field) => void
  onHideField: (field: Field) => void
  /** Told the column's position and whether it is the frozen one at the edge. */
  onFreezeTo: (index: number, frozen: boolean) => void
  /** The field the new one should land in front of, or null for the end. */
  onInsertField: (beside: number | null) => void
  onDuplicateField: (field: Field) => void
  onMakePrimary: (field: Field) => void
  onRedactField: (field: Field) => void
  /** Whether this reader may say who the columns are withheld from. */
  mayGovern: boolean
  /** The columns withheld from this reader, which say so in their heading. */
  redacted: ReadonlySet<number>
  onStartResize: (resize: Resize) => void
}) {
  const { t } = useTranslation()

  return (
    <div
      role="row"
      aria-rowindex={1}
      className="sticky top-0 z-20 flex shrink-0 border-b bg-background"
      // Padding rather than a leading element: the group columns take room at
      // the left of every row, and the sticky cells after it have to stick
      // *past* them rather than against the edge of the container.
      style={{ height: HEADER_HEIGHT, paddingLeft: gutterLeft }}
    >
      <div
        role="columnheader"
        aria-colindex={1}
        className="sticky z-10 flex shrink-0 items-center border-r bg-background px-2 text-xs text-muted-foreground"
        style={{ width: GUTTER, left: gutterLeft }}
      >
        {t('grid.rowNumber')}
      </div>
      {columns.map((column, index) => {
        // The boundary: the last frozen column, and so the only one whose
        // menu offers to unfreeze rather than to freeze up to itself.
        const isFrozenEdge = column.frozen && columns[index + 1]?.frozen !== true

        return (
          <div
            key={column.field.id}
            role="columnheader"
            aria-colindex={index + 2}
            title={column.field.name}
            onDoubleClick={() => {
              onEditField(column.field)
            }}
            className={cn(
              'group/header relative flex shrink-0 items-center gap-1 border-r px-2 text-sm font-medium',
              column.frozen && 'sticky z-10 bg-background',
            )}
            style={{
              width: column.width,
              ...(column.frozen ? { left: offsets[index] } : {}),
            }}
          >
            <span className="truncate">{column.field.name}</span>
            {/* Said in the heading rather than only in each cell, because it is
                a fact about the column: every row of it reads the same, and a
                reader scrolling past forty should not have to infer it. */}
            {redacted.has(column.field.id) && (
              <EyeClosed
                aria-label={t('redaction.redacted')}
                className="size-3 shrink-0 text-muted-foreground"
              />
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  // Out of the tab order for the same reason the row-opening
                  // buttons are: one per column would be a header nobody can tab
                  // past. The dialog is reachable by double-clicking the header.
                  tabIndex={-1}
                  aria-label={t('field.menu', { field: column.field.name })}
                  className="ml-auto rounded p-0.5 opacity-0 group-hover/header:opacity-100 hover:bg-muted focus-visible:opacity-100"
                >
                  <ChevronDown className="size-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem
                  onSelect={() => {
                    onEditField(column.field)
                  }}
                >
                  {t('field.edit')}
                </DropdownMenuItem>
                {/* Not offered for the primary field, which identifies the row
                  and is pinned to the front as a rule rather than as a stored
                  position -- so hiding it is something the grid would refuse to
                  do afterwards. */}
                {!column.field.primary && (
                  <DropdownMenuItem
                    onSelect={() => {
                      onHideField(column.field)
                    }}
                  >
                    <EyeOff className="size-4" />
                    {t('view.hideField')}
                  </DropdownMenuItem>
                )}
                {/*
                  Offered to the database's owner and to the workspace admins,
                  because the endpoint answers only to them -- the same rule the
                  sidebar's table access item follows. A control that 401s reads
                  as a bug rather than as a rule.
                */}
                {mayGovern && (
                  <DropdownMenuItem
                    onSelect={() => {
                      onRedactField(column.field)
                    }}
                  >
                    <EyeClosed className="size-4" />
                    {t('redaction.menu')}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onSelect={() => {
                    onDuplicateField(column.field)
                  }}
                >
                  <Copy className="size-4" />
                  {t('field.duplicate')}
                </DropdownMenuItem>
                {/* Not offered on the one that already is, and not on a field
                  the backend will refuse: a primary field has to be a type
                  that can identify a row, which is the same question
                  `offeredFieldTypes` asks of a field that is already primary. */}
                {!column.field.primary && canBePrimaryField(column.field) && (
                  <DropdownMenuItem
                    onSelect={() => {
                      onMakePrimary(column.field)
                    }}
                  >
                    <KeyRound className="size-4" />
                    {t('field.makePrimary')}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onSelect={() => {
                    onInsertField(column.field.id)
                  }}
                >
                  <ArrowLeftToLine className="size-4" />
                  {t('field.insertLeft')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => {
                    // The field the new one lands *in front of*, which for the
                    // last column is nothing at all -- the end of the row.
                    onInsertField(columns[index + 1]?.field.id ?? null)
                  }}
                >
                  <ArrowRightToLine className="size-4" />
                  {t('field.insertRight')}
                </DropdownMenuItem>
                {/* A menu item rather than the drag handle upstream puts
                    between the frozen columns and the rest. The handle cannot
                    be operated without a pointer, and this is the same action
                    somewhere the keyboard already reaches. Offered only where
                    the backend would accept it: it freezes from the left, and
                    at most four. */}
                {(column.frozen || index < MAX_FROZEN_COLUMNS) && (
                  <DropdownMenuItem
                    onSelect={() => {
                      onFreezeTo(index, isFrozenEdge)
                    }}
                  >
                    <Snowflake className="size-4" />
                    {t(isFrozenEdge ? 'view.unfreeze' : 'view.freezeTo')}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            <div
              // Hidden from assistive technology, which is the honest thing to do
              // about it rather than an oversight. It cannot be operated without
              // a pointer: one focusable divider per column would be forty tab
              // stops in front of a widget the plan deliberately made into one,
              // and announcing a control nobody can reach is worse than not
              // announcing it. Hiding, showing and reordering columns are all
              // reachable from the keyboard, in the bar above; **column width is
              // not**, and the answer to that is keyboard navigation of the
              // header row, which this grid does not have yet.
              aria-hidden
              data-testid={`resize-${column.field.id}`}
              onMouseDown={(event) => {
                // Otherwise the browser starts selecting the header text, and the
                // drag leaves a highlighted column name behind it.
                event.preventDefault()
                onStartResize({
                  fieldId: column.field.id,
                  from: event.clientX,
                  startWidth: column.width,
                  width: column.width,
                })
              }}
              className="absolute top-0 right-0 h-full w-1 cursor-col-resize hover:bg-primary/40"
            />
          </div>
        )
      })}

      {/* At the end of the header rather than in a menu, because adding a
          column is the one thing about the columns that somebody arriving at a
          table with two of them has to be able to find. */}
      <button
        type="button"
        aria-label={t('field.add')}
        onClick={() => {
          onInsertField(null)
        }}
        className="flex w-10 shrink-0 items-center justify-center text-muted-foreground hover:bg-muted"
      >
        <Plus className="size-4" />
      </button>
    </div>
  )
}

function GridRow({
  index,
  row,
  columns,
  offsets,
  gutterLeft,
  endsGroup,
  colors,
  rowHeight,
  identifier,
  context,
  onSelect,
  onExtend,
  onOpen,
  onExpand,
  onDelete,
  onCreate,
  selectedRowIds,
  heldReason,
  saving,
}: {
  index: number
  row: RowData | undefined
  columns: readonly GridColumn[]
  offsets: readonly number[]
  gutterLeft: number
  endsGroup: ReadonlySet<number>
  /** The colours this row is drawn in, already worked out. */
  colors: RowColors
  rowHeight: number
  identifier: RowIdentifier
  context: FieldContext
  onSelect: (cell: Cell, modifiers: { extend: boolean; secondary: boolean }) => boolean
  onExtend: (cell: Cell) => void
  onOpen: () => void
  onExpand: (rowId: number) => void
  onDelete: (rowIds: readonly number[]) => void
  onCreate: (afterIndex: number) => void
  selectedRowIds: () => number[]
  /** Why this row is being held in place, or undefined when it is not. */
  heldReason: HoldReason | undefined
  /** Whether a write for this row is still outstanding. */
  saving: boolean
}) {
  const { t } = useTranslation()

  // A row that has not arrived has nothing to act on, so it is drawn without a
  // menu rather than with one that refuses.
  if (row === undefined) {
    return (
      <RowBody
        index={index}
        row={row}
        columns={columns}
        offsets={offsets}
        gutterLeft={gutterLeft}
        endsGroup={endsGroup}
        colors={colors}
        rowHeight={rowHeight}
        identifier={identifier}
        context={context}
        onSelect={onSelect}
        onExtend={onExtend}
        onOpen={onOpen}
        onExpand={onExpand}
        heldReason={heldReason}
        saving={saving}
      />
    )
  }

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <RowBody
          index={index}
          row={row}
          columns={columns}
          offsets={offsets}
          gutterLeft={gutterLeft}
          endsGroup={endsGroup}
          colors={colors}
          rowHeight={rowHeight}
          identifier={identifier}
          context={context}
          onSelect={onSelect}
          onExtend={onExtend}
          onOpen={onOpen}
          onExpand={onExpand}
          heldReason={heldReason}
          saving={saving}
        />
      </ContextMenuTrigger>
      <ContextMenuContent aria-label={t('grid.rowActions')}>
        <RowActions
          row={row}
          index={index}
          onDelete={onDelete}
          onCreate={onCreate}
          onExpand={onExpand}
          selectedRowIds={selectedRowIds}
        />
      </ContextMenuContent>
    </ContextMenu>
  )
}

/**
 * What the menu offers, worked out when it opens rather than on every render.
 *
 * Radix only mounts the content while the menu is open, so reading the
 * selection here reads it as it is at that moment -- which is after the
 * right-click has moved it, if it moved it.
 */
function RowActions({
  row,
  index,
  onDelete,
  onCreate,
  onExpand,
  selectedRowIds,
}: {
  row: RowData
  index: number
  onDelete: (rowIds: readonly number[]) => void
  onCreate: (afterIndex: number) => void
  onExpand: (rowId: number) => void
  selectedRowIds: () => number[]
}) {
  const { t } = useTranslation()
  const selected = selectedRowIds()
  // The row that was right-clicked, unless it is one of several selected --
  // right-clicking inside a selection acts on the selection, and outside it
  // acts on the row.
  const target = selected.includes(row.id) ? selected : [row.id]

  return (
    <>
      {/* First, and offered for every row: the button in the gutter is the
          other way to open one, and it is only there while the pointer is over
          the row. The row dialog is where a row's comments and its history are
          read, so a row nobody can open is a row nobody can discuss. */}
      <ContextMenuItem
        onSelect={() => {
          onExpand(row.id)
        }}
      >
        {t('grid.expandRow')}
      </ContextMenuItem>
      <ContextMenuItem
        onSelect={() => {
          onCreate(index)
        }}
      >
        {t('grid.insertBelow')}
      </ContextMenuItem>
      <ContextMenuItem
        variant="destructive"
        onSelect={() => {
          onDelete(target)
        }}
      >
        {t('grid.deleteRow', { count: target.length })}
      </ContextMenuItem>
    </>
  )
}

function RowBody({
  index,
  row,
  columns,
  offsets,
  gutterLeft,
  endsGroup,
  colors,
  rowHeight,
  identifier,
  context,
  onSelect,
  onExtend,
  onOpen,
  onExpand,
  heldReason,
  saving,
  ...rest
}: {
  index: number
  row: RowData | undefined
  columns: readonly GridColumn[]
  offsets: readonly number[]
  gutterLeft: number
  endsGroup: ReadonlySet<number>
  colors: RowColors
  rowHeight: number
  identifier: RowIdentifier
  context: FieldContext
  onSelect: (cell: Cell, modifiers: { extend: boolean; secondary: boolean }) => boolean
  onExtend: (cell: Cell) => void
  onOpen: () => void
  onExpand: (rowId: number) => void
  heldReason: HoldReason | undefined
  saving: boolean
}) {
  const { t } = useTranslation()
  // Whether the cell the mouse went down on was already the selected one, which
  // is what says a link inside it may be followed. A ref rather than state: it
  // is read by the click that the same gesture is about to fire, and nothing on
  // screen depends on it.
  const following = useRef(false)

  // The background has to reach the sticky cells as well as the row. They paint
  // their own opaque background -- without it the columns scrolling underneath
  // show through -- so a colour set only on the row would stop at the frozen
  // ones and leave the row looking half-painted.
  const painted =
    colors.background === null
      ? undefined
      : selectColorStyle(colors.background).backgroundColor

  return (
    <div
      {...rest}
      role="row"
      // The header is row one, so the row at absolute index 0 is row two. This
      // is the number that makes a grid navigable when only a hundred of its
      // forty thousand rows are in the document.
      aria-rowindex={index + 2}
      className={cn(
        'group/row absolute flex border-b hover:bg-muted/40',
        // The last row of a group, drawn darker rather than thicker: a heavier
        // border would take a pixel out of the row and shift its text against
        // every other row on screen.
        row !== undefined && endsGroup.has(row.id) && 'border-b-foreground/25',
      )}
      // The same room the header leaves; see the note there.
      style={{
        top: index * rowHeight,
        height: rowHeight,
        paddingLeft: gutterLeft,
        ...(painted === undefined ? {} : { backgroundColor: painted }),
      }}
    >
      <div
        role="rowheader"
        aria-colindex={1}
        className="sticky z-10 flex shrink-0 items-center gap-1 border-r bg-background px-2 text-xs text-muted-foreground"
        style={{
          width: GUTTER,
          left: gutterLeft,
          ...(painted === undefined ? {} : { backgroundColor: painted }),
          // On the gutter rather than on the row, so the bar sticks where the
          // gutter does and stays visible however far the columns are scrolled
          // -- and so the group columns, which overlay the left of the row, do
          // not cover it.
          ...(colors.leftBorder === null
            ? {}
            : {
                borderLeftWidth: 4,
                borderLeftStyle: 'solid' as const,
                borderLeftColor: selectColorStyle(colors.leftBorder).backgroundColor,
              }),
        }}
      >
        {row === undefined ? '' : identifier === 'count' ? index + 1 : row.id}
        {saving && (
          <Loader2
            aria-label={t('row.saving')}
            className="size-3 shrink-0 animate-spin"
          />
        )}
        {row !== undefined && (
          <button
            type="button"
            // Out of the tab order on purpose. One of these per visible row
            // would be eighty tab stops inside a widget the plan deliberately
            // made into one; shift-Enter is the keyboard's way in.
            tabIndex={-1}
            aria-label={t('row.open', { id: row.id })}
            onClick={() => {
              onExpand(row.id)
            }}
            className="ml-auto rounded p-0.5 opacity-0 group-hover/row:opacity-100 hover:bg-muted focus-visible:opacity-100"
          >
            <Maximize2 className="size-3" />
          </button>
        )}
      </div>
      {heldReason !== undefined && (
        // Sticky to the right so it stays on screen however far the columns are
        // scrolled, and `pointer-events-none` so it never swallows a click
        // meant for the cell underneath it.
        <div
          role="status"
          className="pointer-events-none sticky right-2 z-20 my-auto ml-auto flex shrink-0 items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-xs whitespace-nowrap text-amber-950 dark:bg-amber-950 dark:text-amber-100"
        >
          <TriangleAlert className="size-3" />
          {t(`row.held_${heldReason}`)}
        </div>
      )}
      {columns.map((column, position) => (
        <div
          key={column.field.id}
          id={row === undefined ? undefined : cellId(row.id, column.field.id)}
          role="gridcell"
          aria-colindex={position + 2}
          // `mousedown` rather than `click`: a selection that only appeared when
          // the button came back up would lag a drag by its whole length.
          onMouseDown={
            row === undefined
              ? undefined
              : (event) => {
                  following.current = onSelect(
                    { rowId: row.id, fieldId: column.field.id },
                    { extend: event.shiftKey, secondary: event.button === 2 },
                  )
                }
          }
          // Dragging across cells extends the selection, which is how a range
          // is picked without reaching for shift. Only while a button is held
          // -- `buttons` is the state now, not the button that opened the
          // gesture -- and never on the secondary button, which is opening a
          // menu over whatever is already selected.
          onMouseEnter={
            row === undefined
              ? undefined
              : (event) => {
                  if (event.buttons !== 1) return
                  onExtend({ rowId: row.id, fieldId: column.field.id })
                }
          }
          // The click that selected a cell does not also follow the link in it.
          // Captured on the cell rather than handled on the link, so that a
          // cell reader is never the thing that has to know about selection --
          // and narrowed to links, so that nothing else a click does inside a
          // cell is cancelled along with it.
          onClickCapture={(event) => {
            if (following.current) return
            if ((event.target as HTMLElement).closest('a') !== null) {
              event.preventDefault()
            }
          }}
          // Wrapped rather than passed straight through: React hands a click
          // handler the event, and `openEditor` reads its first argument as the
          // text to start from -- so the cell would open holding an object.
          onDoubleClick={
            row === undefined
              ? undefined
              : () => {
                  onOpen()
                }
          }
          className={cn(
            'flex shrink-0 items-center overflow-hidden border-r px-2 text-sm',
            column.frozen && 'sticky z-10 bg-background',
          )}
          style={{
            width: column.width,
            ...(column.frozen ? { left: offsets[position] } : {}),
            ...(column.frozen && painted !== undefined
              ? { backgroundColor: painted }
              : {}),
          }}
        >
          {row === undefined ? (
            // A row inside the viewport but outside the window: the reader has
            // scrolled past what is held and the fetch has not landed. A blank
            // cell of the right size is the honest thing to draw -- the row
            // exists, its contents are not known yet.
            <span className="h-3 w-2/3 animate-pulse rounded bg-muted" />
          ) : (
            <CellDisplay field={column.field} row={row} context={context} />
          )}
        </div>
      ))}
    </div>
  )
}
